import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Eye, EyeOff, Sparkles, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBlog() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [showGenModal, setShowGenModal] = useState(false);
    const [topicHint, setTopicHint] = useState("");
    const [suggestions, setSuggestions] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/blog");
            setArticles(data);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        api.get("/admin/blog/topics/suggestions")
            .then(({ data }) => setSuggestions(data.topics || []))
            .catch(() => {});
    }, []);

    const togglePublish = async (a) => {
        try {
            const { data } = await api.put(`/admin/blog/${a.id}`, { published: !a.published });
            setArticles(articles.map((x) => (x.id === a.id ? data : x)));
            toast.success(data.published ? "Article publié" : "Article dépublié");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Supprimer l'article "${a.title}" ?`)) return;
        try {
            await api.delete(`/admin/blog/${a.id}`);
            setArticles(articles.filter((x) => x.id !== a.id));
            toast.success("Article supprimé");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const generateArticle = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/admin/blog/generate", {
                topic: topicHint.trim() || undefined,
            });
            setArticles([data, ...articles]);
            toast.success(`Article généré : "${data.title}"`);
            setShowGenModal(false);
            setTopicHint("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="p-10" data-testid="admin-blog-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <span className="overline">Espace admin</span>
                    <h1 className="mt-3 font-serif text-4xl text-brand-ink font-light">Articles du blog</h1>
                    <p className="mt-3 text-sm text-brand-sageDark">
                        L'IA génère automatiquement 2 brouillons par semaine (lundi + jeudi). Validez et publiez-les en un clic.
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowGenModal(true)}
                        data-testid="admin-blog-generate-ai"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-sage text-brand-bg text-xs uppercase tracking-[0.2em] hover:bg-brand-sageDark transition-colors"
                    >
                        <Sparkles size={14} /> Générer avec l'IA
                    </button>
                    <Link
                        to="/admin/blog/new"
                        data-testid="admin-blog-new"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.2em] hover:bg-brand-sage transition-colors"
                    >
                        <Plus size={14} /> Nouvel article
                    </Link>
                </div>
            </div>

            {loading ? (
                <p className="mt-12 text-brand-sageDark">Chargement…</p>
            ) : error ? (
                <p className="mt-12 text-red-700">{error}</p>
            ) : articles.length === 0 ? (
                <div className="mt-12 py-16 text-center bg-brand-bgalt border border-brand-border" data-testid="admin-blog-empty">
                    <p className="font-serif text-xl text-brand-ink italic">Aucun article pour l'instant.</p>
                    <button
                        onClick={() => setShowGenModal(true)}
                        className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-brand-sage border-b border-brand-sage pb-1"
                    >
                        Générer le premier article avec l'IA
                    </button>
                </div>
            ) : (
                <div className="mt-10 bg-brand-bgalt border border-brand-border divide-y divide-brand-border">
                    {articles.map((a) => (
                        <div key={a.id} className="p-5 flex items-center justify-between gap-4" data-testid={`blog-row-${a.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    {a.ai_generated && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-brand-sage" title="Généré par IA">
                                            <Sparkles size={10} /> IA
                                        </span>
                                    )}
                                </div>
                                <p className="font-serif text-lg text-brand-ink truncate">{a.title}</p>
                                <p className="text-sm text-brand-sageDark mt-0.5 truncate">
                                    /{a.slug} · {new Date(a.updated_at).toLocaleDateString("fr-FR")}
                                </p>
                            </div>
                            <span className={`text-xs uppercase tracking-[0.16em] px-3 py-1 shrink-0 ${a.published ? "bg-brand-sage text-brand-bg" : "border border-brand-border text-brand-sageDark"}`}>
                                {a.published ? "Publié" : "Brouillon"}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => togglePublish(a)}
                                    className="p-2 hover:bg-brand-sage/10 text-brand-sageDark"
                                    title={a.published ? "Dépublier" : "Publier"}
                                    data-testid={`blog-publish-${a.id}`}
                                >
                                    {a.published ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                                <Link
                                    to={`/admin/blog/${a.id}`}
                                    className="p-2 hover:bg-brand-sage/10 text-brand-sageDark"
                                    title="Modifier"
                                    data-testid={`blog-edit-${a.id}`}
                                >
                                    <Pencil size={16} />
                                </Link>
                                <button
                                    onClick={() => remove(a)}
                                    className="p-2 hover:bg-red-50 text-red-700"
                                    title="Supprimer"
                                    data-testid={`blog-delete-${a.id}`}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* GENERATE MODAL */}
            {showGenModal && (
                <div className="fixed inset-0 z-50 bg-brand-ink/60 flex items-center justify-center px-6" data-testid="gen-modal">
                    <div className="bg-brand-bg max-w-xl w-full p-10 border border-brand-border">
                        <div className="flex items-center gap-2 overline mb-4">
                            <Sparkles size={12} /> Génération IA — Claude Sonnet
                        </div>
                        <h2 className="font-serif text-3xl text-brand-ink font-light">Générer un nouvel article</h2>
                        <p className="mt-3 text-sm text-brand-sageDark leading-relaxed">
                            Laissez le champ vide pour laisser l'IA choisir un sujet dans le calendrier éditorial,
                            ou précisez un sujet (ex. <em>"Rénovation cuisine Paris 8e"</em>).
                            <br />L'article sera créé en brouillon, prêt pour votre relecture.
                        </p>

                        <label className="overline block mt-8 mb-3">Sujet (optionnel)</label>
                        <input
                            type="text"
                            value={topicHint}
                            onChange={(e) => setTopicHint(e.target.value)}
                            placeholder="Ex. Tendances cuisine 2026"
                            className="field-input"
                            data-testid="gen-modal-topic"
                            disabled={generating}
                        />

                        {suggestions.length > 0 && (
                            <div className="mt-5">
                                <p className="text-xs uppercase tracking-[0.18em] text-brand-sageDark mb-2">Suggestions</p>
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                    {suggestions.slice(0, 8).map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setTopicHint(s)}
                                            disabled={generating}
                                            className="text-xs px-3 py-1.5 border border-brand-border text-brand-sageDark hover:border-brand-sage hover:text-brand-sage text-left"
                                        >
                                            {s.length > 60 ? s.slice(0, 60) + "…" : s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-10 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowGenModal(false)}
                                disabled={generating}
                                data-testid="gen-modal-cancel"
                                className="px-6 py-3 text-xs uppercase tracking-[0.2em] text-brand-sageDark hover:text-brand-ink"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                onClick={generateArticle}
                                disabled={generating}
                                data-testid="gen-modal-submit"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.2em] hover:bg-brand-sage transition-colors disabled:opacity-60"
                            >
                                {generating ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        Génération en cours…
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={14} /> Lancer la génération
                                    </>
                                )}
                            </button>
                        </div>
                        {generating && (
                            <p className="mt-4 text-xs text-brand-sageDark italic">Comptez 30 à 60 secondes — l'IA rédige un article complet d'environ 1000 mots optimisé SEO.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
